import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import {
  PlusOutlined,
  SendOutlined,
  SwapOutlined,
  TeamOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import CustomButton from 'components/custom-button';
import NoteActionModal, { NoteActionConfig } from 'pages/star-shared/components/note-action-modal';
import { NoWrap } from 'pages/star-shared/components/status-tags';
import type {
  StudioListItem,
  StudioMemberBase,
  StudioMemberRow,
  StudioRequestKind,
  StudioSummary,
} from '../contract';
import {
  STUDIO_MEMBERS_LIMIT,
  StudioViewer,
  addStudioMembers,
  getStudioMembers,
  getStudioTeams,
  listStudios,
  moveStudioMember,
  removeStudioMember,
  studioAbilities,
  useStudioLoad,
} from '../api';
import { mapStudioError } from '../studio-error';
import {
  EMPTY_TEXT,
  MEMBER_SOURCE_LABEL,
  MEMBER_SOURCE_OPTIONS,
  MEMBER_TYPE_FILTER_OPTIONS,
  PENDING_ADMIN_TAG,
  REASON_MAX,
  REASON_MIN,
  REQUEST_KIND_LABEL,
  TEAM_ROLE_LABEL,
  familyMismatchWarning,
  membersSummaryLine,
} from '../labels';
import { displayName, formatDay, formatHours, formatKc, utcMonth } from '../format';
import MemberCell from '../components/member-cell';
import AddMembersModal from '../components/add-members-modal';
import AssignTeamModal, { AssignMember } from '../components/assign-team-modal';
import RequestModal, { RequestModalMember } from '../components/request-modal';
import { StudioLoadError } from './studio-header';
import styles from '../styles.module.scss';

export interface MembersTabProps {
  studioId: number;
  studio: StudioSummary;
  viewer: StudioViewer;
  /** Tháng KC (tham số `month` trên URL, giữ khi đổi tab); không có = tháng UTC hiện tại. */
  month?: string;
  onMonthChange: (month: string) => void;
  /** Sau mỗi thao tác (tải lại số của trang: đề nghị chờ, thành viên…). */
  onChanged?: () => void;
}

type MemberType = 'CREATOR' | 'STAFF' | 'ASSISTANT';
type RequestTarget = {
  kind: Exclude<StudioRequestKind, 'TEAM_CHANNEL'>;
  member?: RequestModalMember | null;
};

const PENALTY_PAGE = '/star/penalties';
const TEAMS_FAILED_TEXT = 'Không tải được danh sách Nhóm nhảy.';

const memberName = (m: StudioMemberRow) =>
  displayName({ nickName: m.nickName, username: m.username, uid: m.uid });
const toAssign = (m: StudioMemberRow): AssignMember => ({
  key: m.key,
  name: memberName(m),
  team: m.team ? { id: m.team.id, name: m.team.name } : null,
});
const toRequestMember = (m: StudioMemberRow): RequestModalMember => ({
  key: m.key,
  userId: m.userId,
  uid: m.uid,
  nickName: m.nickName,
  username: m.username,
  avatar: m.avatar,
});
const hasPending = (m: StudioMemberRow, kind: StudioRequestKind) =>
  (m.pendingRequests || []).some((p) => p.requestKind === kind);

// ------------------------------------------------------- Chuyển Studio (S13) ---

interface MoveStudioModalProps {
  studioId: number;
  member: StudioMemberRow | null;
  onClose: () => void;
  onDone: () => void;
}

/** Admin chuyển một thành viên sang Studio khác (S13): chọn Studio đích (tìm bằng S2) + lý do ≥ 5 ký tự. */
const MoveStudioModal = ({ studioId, member, onClose, onDone }: MoveStudioModalProps) => {
  const [form] = Form.useForm<{ targetStudioId?: number; reason: string }>();
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState<StudioListItem[]>([]);
  const [fetching, setFetching] = useState(false);
  const open = !!member;

  const search = useMemo(
    () =>
      debounce(async (text: string) => {
        setFetching(true);
        try {
          const res = await listStudios({ q: text.trim() || undefined, limit: 20, sort: 'title' });
          setOptions((res?.data || []).filter((s) => s.id !== studioId));
        } catch (err) {
          setOptions([]);
          notification.error({ message: mapStudioError(err) });
        } finally {
          setFetching(false);
        }
      }, 400),
    [studioId]
  );

  useEffect(() => {
    if (open) search('');
    return () => search.cancel();
  }, [open, search]);

  const handleClose = () => {
    if (saving) return;
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: { targetStudioId?: number; reason: string }) => {
    if (!member || !values.targetStudioId) return;
    const target = options.find((s) => s.id === values.targetStudioId);
    setSaving(true);
    try {
      await moveStudioMember(studioId, member.key, {
        targetStudioId: values.targetStudioId,
        reason: values.reason.trim(),
      });
      notification.success({
        message: `Đã chuyển ${memberName(member)} sang ${target?.title || 'Studio khác'}.`,
      });
      form.resetFields();
      onDone();
      onClose();
    } catch (err) {
      notification.error({ message: mapStudioError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={member ? `Chuyển Studio · ${memberName(member)}` : 'Chuyển Studio'}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      width={520}
    >
      <Form form={form} layout="vertical" className="custom-form" onFinish={onFinish}>
        <Form.Item
          label="Studio mới"
          name="targetStudioId"
          rules={[{ required: true, message: 'Chọn Studio mới.' }]}
        >
          <Select
            showSearch
            filterOption={false}
            placeholder="Tìm theo tên Studio hoặc tên Quản lý"
            onSearch={(text) => search(text)}
            loading={fetching}
            notFoundContent={fetching ? 'Đang tìm…' : 'Không có Studio phù hợp.'}
            options={options.map((s) => ({
              value: s.id,
              label: s.manager ? `${s.title} · ${displayName(s.manager)}` : s.title,
            }))}
          />
        </Form.Item>
        <Form.Item
          label="Lý do"
          name="reason"
          rules={[
            {
              validator: (_, value?: string) =>
                (value || '').trim().length >= REASON_MIN
                  ? Promise.resolve()
                  : Promise.reject(new Error(`Lý do cần ít nhất ${REASON_MIN} ký tự.`)),
            },
          ]}
        >
          <Input.TextArea rows={3} maxLength={REASON_MAX} showCount placeholder="Nhập lý do" />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text="Chuyển Studio"
            bgColor="secondary-green"
            htmlType="submit"
            loading={saving}
          />
        </div>
      </Form>
    </Modal>
  );
};

// ------------------------------------------------------------------- tab ---

/**
 * Tab Thành viên của Chi tiết Studio (SPEC Studio mục 8.7.5): bảng S10 có bộ lọc, dòng tóm tắt, cảnh báo
 * tài khoản ghi ở Studio này mà UID thuộc nhóm file khác (Admin / SMOD). Nút theo người xem: Quản lý Studio
 * xếp Nhóm nhảy + gửi đề nghị (Admin duyệt mới làm), Admin thao tác trực tiếp, SMOD chỉ xem.
 */
const MembersTab = ({
  studioId,
  studio,
  viewer,
  month,
  onMonthChange,
  onChanged,
}: MembersTabProps) => {
  const abilities = studioAbilities(viewer.mine, studioId);
  const isManager = abilities.request;
  const isAdmin = abilities.admin;
  const shownMonth = month || utcMonth();

  const [searchText, setSearchText] = useState('');
  const [q, setQ] = useState('');
  const [source, setSource] = useState<StudioMemberBase['source'] | undefined>();
  const [type, setType] = useState<MemberType | undefined>();
  const [teamId, setTeamId] = useState<number | 'none' | undefined>();
  const [inactive, setInactive] = useState(false);
  const [page, setPage] = useState(1);

  const [assign, setAssign] = useState<AssignMember | null>(null);
  const [request, setRequest] = useState<RequestTarget | null>(null);
  const [adding, setAdding] = useState(false);
  const [moving, setMoving] = useState<StudioMemberRow | null>(null);
  const [action, setAction] = useState<NoteActionConfig | null>(null);
  const [linkingId, setLinkingId] = useState<number | null>(null);

  const members = useStudioLoad(
    () =>
      getStudioMembers(studioId, {
        month,
        q: q || undefined,
        source,
        type,
        teamId,
        inactive,
        page,
        limit: STUDIO_MEMBERS_LIMIT,
      }),
    [studioId, month, q, source, type, teamId, inactive, page],
    { resetKey: studioId }
  );
  // Nhóm nhảy ACTIVE: bộ lọc "Nhóm nhảy" và modal xếp nhóm. Lỗi ở đây không chặn bảng nhưng phải báo,
  // và khoá nút "Xếp vào Nhóm nhảy" (modal không có nhóm nào để chọn).
  const teams = useStudioLoad(() => getStudioTeams(studioId, { status: 'ACTIVE' }), [studioId], {
    resetKey: studioId,
  });
  const teamList = teams.data?.data || [];
  const teamsFailed = !!teams.error && !teams.data;

  const data = members.data;
  const rows = data?.data || [];
  const summary = data?.summary;
  const filtered = !!(q || source || type || teamId || inactive);

  const afterChange = () => {
    members.reload();
    onChanged?.();
  };
  const resetPage = () => setPage(1);

  const openRemove = (m: StudioMemberRow) =>
    setAction({
      title: 'Gỡ khỏi Studio',
      description: (
        <span>
          {memberName(m)} sẽ không còn là thành viên của {studio.title}. Dữ liệu kim cương từ TikTok
          không bị xoá.
        </span>
      ),
      noteRequired: true,
      noteLabel: `Lý do gỡ (ít nhất ${REASON_MIN} ký tự)`,
      confirmText: 'Gỡ',
      danger: true,
      onSubmit: async (note) => {
        if ((note || '').length < REASON_MIN) {
          notification.error({ message: `Lý do cần ít nhất ${REASON_MIN} ký tự.` });
          throw new Error('REASON_TOO_SHORT');
        }
        try {
          await removeStudioMember(studioId, m.key, note as string);
          notification.success({ message: `Đã gỡ ${memberName(m)} khỏi Studio.` });
          afterChange();
        } catch (err) {
          notification.error({ message: mapStudioError(err) });
          throw err;
        }
      },
    });

  // Admin gán tay tài khoản "lệch" (familyId = Studio này, Studio hiệu lực khác) vào Studio này (S11).
  const linkMismatch = async (userId: number, name: string) => {
    setLinkingId(userId);
    try {
      const res = await addStudioMembers(studioId, { userIds: [userId] });
      const moved = res?.moved ? ' (chuyển từ Studio khác)' : '';
      notification.success({ message: `Đã thêm ${name} vào Studio này${moved}.` });
      afterChange();
    } catch (err) {
      notification.error({ message: mapStudioError(err) });
    } finally {
      setLinkingId(null);
    }
  };

  const renderActions = (m: StudioMemberRow) => {
    const buttons: ReactNode[] = [];
    if (abilities.manageTeams) {
      buttons.push(
        <Tooltip key="team" title={teamsFailed ? TEAMS_FAILED_TEXT : undefined}>
          <Button
            size="small"
            icon={<TeamOutlined />}
            disabled={teamsFailed}
            onClick={() => setAssign(toAssign(m))}
          >
            Xếp vào Nhóm nhảy
          </Button>
        </Tooltip>
      );
    }
    if (isManager) {
      if (m.userId && m.uid) {
        const pending = hasPending(m, 'KC_MANUAL');
        buttons.push(
          <Tooltip key="kc" title={pending ? PENDING_ADMIN_TAG : undefined}>
            <Button
              size="small"
              icon={<SendOutlined />}
              disabled={pending}
              onClick={() => setRequest({ kind: 'KC_MANUAL', member: toRequestMember(m) })}
            >
              Đề nghị nhập KC
            </Button>
          </Tooltip>
        );
      }
      const pendingRemove = hasPending(m, 'MEMBER_REMOVE');
      buttons.push(
        <Tooltip key="remove" title={pendingRemove ? PENDING_ADMIN_TAG : undefined}>
          <Button
            size="small"
            danger
            icon={<UserDeleteOutlined />}
            disabled={pendingRemove}
            onClick={() => setRequest({ kind: 'MEMBER_REMOVE', member: toRequestMember(m) })}
          >
            Đề nghị gỡ
          </Button>
        </Tooltip>
      );
      if (m.userId) {
        // Trang Phạt có sẵn chỉ đọc tham số `tab`: Quản lý tự chọn người trong modal Ghi phạt (mục 8.7.5).
        buttons.push(
          <Link key="penalty" to={PENALTY_PAGE}>
            <Button size="small">Ghi phạt</Button>
          </Link>
        );
      }
    }
    if (isAdmin) {
      buttons.push(
        <Button key="move" size="small" icon={<SwapOutlined />} onClick={() => setMoving(m)}>
          Chuyển Studio
        </Button>,
        <Button
          key="remove"
          size="small"
          danger
          icon={<UserDeleteOutlined />}
          onClick={() => openRemove(m)}
        >
          Gỡ
        </Button>
      );
    }
    return buttons.length ? (
      <Space size={4} wrap>
        {buttons}
      </Space>
    ) : null;
  };

  const withActions = abilities.manageTeams || isManager || isAdmin;
  const columns: ColumnsType<StudioMemberRow> = [
    {
      title: 'Creator',
      key: 'member',
      width: 260,
      fixed: 'left',
      render: (_, m) => (
        <MemberCell
          nickName={m.nickName}
          username={m.username}
          uid={m.uid}
          avatar={m.avatar}
          memberType={m.memberType}
          inactive={m.inactive}
          channelOf={m.channelOf}
          sub={null}
          extra={
            m.pendingRequests?.length ? (
              <Tooltip
                title={m.pendingRequests
                  .map((p) => REQUEST_KIND_LABEL[p.requestKind] || p.requestKind)
                  .join(', ')}
              >
                <Tag color="gold">{PENDING_ADMIN_TAG}</Tag>
              </Tooltip>
            ) : undefined
          }
        />
      ),
    },
    {
      title: 'TikTok ID',
      key: 'username',
      width: 150,
      render: (_, m) => (m.username ? `@${m.username}` : '—'),
    },
    {
      title: 'UID TikTok',
      key: 'uid',
      width: 190,
      render: (_, m) => m.uid || '—',
    },
    {
      title: 'Nguồn',
      key: 'source',
      width: 170,
      render: (_, m) => (
        <div>
          <div>{MEMBER_SOURCE_LABEL[m.source] || m.source}</div>
          {m.fileGroupName && <div className={styles.muted}>{m.fileGroupName}</div>}
        </div>
      ),
    },
    {
      title: 'Nhóm nhảy',
      key: 'team',
      width: 170,
      render: (_, m) =>
        m.team ? (
          <span className={styles.tags}>
            <span>{m.team.name}</span>
            {m.team.role === 'LEADER' && <Tag color="purple">{TEAM_ROLE_LABEL.LEADER}</Tag>}
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: 'KC tháng',
      key: 'kc',
      width: 130,
      align: 'right',
      render: (_, m) => <NoWrap>{m.kc?.present ? formatKc(m.kc.diamonds) : '—'}</NoWrap>,
    },
    {
      title: 'Ngày LIVE',
      key: 'liveDays',
      width: 100,
      align: 'right',
      render: (_, m) => (m.kc?.present ? formatKc(m.kc.liveDays) : '—'),
    },
    {
      title: 'Giờ LIVE',
      key: 'liveHours',
      width: 100,
      align: 'right',
      render: (_, m) => (m.kc?.present ? formatHours(m.kc.liveSeconds) : '—'),
    },
    {
      title: 'Vào Studio',
      key: 'joined',
      width: 120,
      render: (_, m) => <NoWrap>{formatDay(m.joinedStudioAt)}</NoWrap>,
    },
    {
      title: 'Tài khoản app',
      key: 'account',
      width: 200,
      render: (_, m) =>
        m.email ? (
          <span style={{ overflowWrap: 'anywhere' }}>{m.email}</span>
        ) : m.userId ? (
          'Đã liên kết'
        ) : (
          <span className={styles.muted}>Chưa liên kết</span>
        ),
    },
    ...(withActions
      ? ([
          {
            title: 'Thao tác',
            key: 'actions',
            width: isManager ? 330 : 300,
            render: (_, m) => renderActions(m),
          },
        ] as ColumnsType<StudioMemberRow>)
      : []),
  ];

  const emptyText = filtered
    ? 'Không có thành viên phù hợp bộ lọc.'
    : `${EMPTY_TEXT.members}${
        isAdmin ? ` ${EMPTY_TEXT.membersAdmin}` : isManager ? ` ${EMPTY_TEXT.membersManager}` : ''
      }`;

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Input.Search
            allowClear
            placeholder="Tìm tên, TikTok ID hoặc UID"
            maxLength={100}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(text) => {
              setQ(text.trim());
              resetPage();
            }}
            style={{ width: 260 }}
          />
          <Select
            allowClear
            placeholder="Nguồn"
            value={source}
            options={MEMBER_SOURCE_OPTIONS}
            onChange={(v) => {
              setSource(v);
              resetPage();
            }}
            style={{ width: 140 }}
          />
          <Select
            allowClear
            placeholder="Loại"
            value={type}
            options={MEMBER_TYPE_FILTER_OPTIONS}
            onChange={(v) => {
              setType(v);
              resetPage();
            }}
            style={{ width: 140 }}
          />
          <Select<number | 'none'>
            allowClear
            placeholder="Nhóm nhảy"
            value={teamId}
            options={[
              { value: 'none', label: 'Chưa xếp nhóm' },
              ...teamList.map((t) => ({ value: t.id, label: t.name })),
            ]}
            onChange={(v) => {
              setTeamId(v);
              resetPage();
            }}
            style={{ width: 170 }}
          />
          <DatePicker
            picker="month"
            format="MM/YYYY"
            allowClear={false}
            value={dayjs(`${shownMonth}-01`)}
            onChange={(v) => {
              if (!v) return;
              onMonthChange(v.format('YYYY-MM'));
              resetPage();
            }}
            aria-label="Tháng"
          />
          <Space size={6}>
            <Switch
              checked={inactive}
              onChange={(checked) => {
                setInactive(checked);
                resetPage();
              }}
              aria-label="Chỉ người không còn trong file"
            />
            <span>Chỉ người không còn trong file</span>
          </Space>
        </div>
        {isManager && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setRequest({ kind: 'MEMBER_ADD' })}
          >
            Đề nghị thêm người
          </Button>
        )}
        {isAdmin && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
            Thêm thành viên
          </Button>
        )}
      </div>

      {members.error && <StudioLoadError error={members.error} onRetry={members.reload} />}
      {teamsFailed && (
        <Alert
          type="warning"
          showIcon
          message={`${TEAMS_FAILED_TEXT} Bộ lọc Nhóm nhảy và nút "Xếp vào Nhóm nhảy" tạm chưa dùng được.`}
          action={
            <Button size="small" loading={teams.loading} onClick={teams.reload}>
              Thử lại
            </Button>
          }
        />
      )}

      {summary && <div className={styles.summaryLine}>{membersSummaryLine(summary)}</div>}

      {abilities.seesFlags && summary && summary.familyMismatch > 0 && (
        <Alert
          type="warning"
          showIcon
          message={familyMismatchWarning(summary.familyMismatch)}
          description={
            data?.mismatches?.length ? (
              <ul className={styles.context}>
                {data.mismatches.map((mm) => (
                  <li key={mm.userId} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span>
                      {mm.name} · Studio hiệu lực:{' '}
                      {mm.effectiveStudio
                        ? mm.effectiveStudio.title || `Studio #${mm.effectiveStudio.id}`
                        : 'chưa thuộc Studio nào'}
                    </span>
                    {isAdmin && (
                      <Popconfirm
                        title={`Thêm ${mm.name} vào Studio này?`}
                        okText="Thêm"
                        cancelText="Huỷ"
                        onConfirm={() => linkMismatch(mm.userId, mm.name)}
                      >
                        <Button size="small" type="link" loading={linkingId === mm.userId}>
                          Thêm vào Studio này
                        </Button>
                      </Popconfirm>
                    )}
                  </li>
                ))}
              </ul>
            ) : undefined
          }
        />
      )}

      <div className="custom-table">
        <Table<StudioMemberRow>
          columns={columns}
          rowKey="key"
          dataSource={rows}
          loading={members.loading}
          pagination={false}
          locale={{ emptyText }}
          scroll={{ x: withActions ? 2000 : 1700 }}
        />
        {data && data.metadata.total > STUDIO_MEMBERS_LIMIT && (
          <Pagination
            showSizeChanger={false}
            showLessItems
            current={page}
            pageSize={STUDIO_MEMBERS_LIMIT}
            total={data.metadata.total}
            onChange={setPage}
          />
        )}
      </div>

      <AssignTeamModal
        open={!!assign}
        studioId={studioId}
        teams={teamList}
        members={assign ? [assign] : undefined}
        onClose={() => setAssign(null)}
        onDone={() => {
          teams.reload();
          afterChange();
        }}
      />
      {request && (
        <RequestModal
          open
          studioId={studioId}
          requestKind={request.kind}
          member={request.member}
          coverage={data?.coverage}
          onClose={() => setRequest(null)}
          onDone={afterChange}
        />
      )}
      <AddMembersModal
        open={adding}
        studioId={studioId}
        studioTitle={studio.title}
        onClose={() => setAdding(false)}
        onDone={afterChange}
      />
      <MoveStudioModal
        studioId={studioId}
        member={moving}
        onClose={() => setMoving(null)}
        onDone={afterChange}
      />
      <NoteActionModal config={action} onClose={() => setAction(null)} />
    </div>
  );
};

export default MembersTab;
